import { z } from 'zod';

import {
  DepositCredentials,
  DepositMethod,
  DepositStatus,
} from 'data/payments/models';

import { Acquirer } from './acquirer';

export const MerchantAccountRole = z.enum(['OWNER', 'ACCOUNTANT', 'SUPPORT']);
export type MerchantAccountRole = z.infer<typeof MerchantAccountRole>;

export const Merchant = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string(),
  role: MerchantAccountRole,
  insertedAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  apiPasswordGenerated: z.boolean(),
  fixedAddress: z.string().nullish(),
});
export type Merchant = z.infer<typeof Merchant>;

export const ApiPasswordResponse = z.object({
  apiPassword: z.string(),
});
export type ApiPasswordResponse = z.infer<typeof ApiPasswordResponse>;

export const Balance = z.object({
  acquirer: Acquirer,
  amount: z.string(),
  currency: z.string(),
  payouts: z.object({
    availableBalance: z.string(),
    fee: z.string(),
    rate: z.string(),
    currency: z.string(),
    methods: z
      .object({
        method: z.string(),
        rate: z.string(),
        fee: z.string(),
        currency: z.string().optional(),
      })
      .array()
      .default([]),
    limits: z
      .object({
        periodStart: z.coerce.date(),
        periodEnd: z.coerce.date(),
        limit: z.string(),
        availableAmount: z.string(),
      })
      .array(),
  }),
  payments: z.object({
    fee: z.string(),
    rate: z.string(),
    currency: z.string(),
    methods: z
      .object({
        method: DepositMethod,
        rate: z.string(),
        fee: z.string(),
        currency: z.string(),
      })
      .array(),
    limits: z
      .object({
        periodStart: z.coerce.date(),
        periodEnd: z.coerce.date(),
        limit: z.string(),
        availableAmount: z.string(),
      })
      .array(),
  }),
});
export type Balance = z.infer<typeof Balance>;

export const BalanceData = z.object({
  totalBalance: z.object({
    amount: z.string(),
    currency: z.string(),
  }),
  balances: Balance.array(),
});
export type BalanceData = z.infer<typeof BalanceData>;

export const DepositOverview = z.object({
  id: z.string().uuid(),
  merchantId: z.string().uuid(),
  customerId: z.string().nullable(),

  acquirer: Acquirer,

  merchantDepositId: z.string(),
  responseUrl: z.string(),
  successRedirect: z.string().nullable(),
  failureRedirect: z.string().nullable(),

  paymentAmount: z.string(),
  paymentCurrency: z.string().nullable(),
  credentials: DepositCredentials.nullable(),

  receivedAmount: z.string(),
  receivedCurrency: z.string().nullable(),

  status: DepositStatus,
  customerName: z.string().nullable(),
  paymentLink: z.string().nullable(),

  detailsUpdatedAt: z.coerce.date(),
  statusUpdatedAt: z.coerce.date(),
  insertedAt: z.coerce.date(),
  expiryDate: z.coerce.date(),
});
export type DepositOverview = z.infer<typeof DepositOverview>;

export const WithdrawalStatus = z.enum([
  'CREATED',
  'SUBMITTED',
  'MANUAL_PROCESSING',
  'EXCHANGE_PROCESSING',
  'SENT',
  'FAILED',
  'FROZEN',
]);
export type WithdrawalStatus = z.infer<typeof WithdrawalStatus>;

export const Withdrawal = z.object({
  id: z.string().uuid(),
  merchantId: z.string(),

  merchantWithdrawalId: z.string().nullable(),
  responseUrl: z.string().nullable(),

  acquirer: Acquirer,

  status: WithdrawalStatus,

  amount: z.string(),
  receivedAmount: z.string(),
  currency: z.string(),
  network: z.string(),
  address: z.string(),
  txId: z.string().nullable(),

  statusUpdatedAt: z.coerce.date(),
  insertedAt: z.coerce.date(),
});
export type Withdrawal = z.infer<typeof Withdrawal>;

export const TopUpStatus = z.enum([
  'CREATED',
  'MONEY_RECEIVED',
  'EXCHANGE_PROCESSING',
  'PAID',
  'EXPIRED',
  'CANCELLED',
  'FROZEN',
]);
export type TopUpStatus = z.infer<typeof TopUpStatus>;

export const TopUp = z.object({
  id: z.string().uuid(),
  merchantId: z.string().uuid(),

  merchantTopUpId: z.string().nullable(),
  responseUrl: z.string().nullable(),

  acquirer: Acquirer,
  address: z.string(),

  amount: z.string(),
  currency: z.string(),

  status: TopUpStatus,

  statusUpdatedAt: z.coerce.date(),
  insertedAt: z.coerce.date(),
  expiryDate: z.coerce.date(),
});
export type TopUp = z.infer<typeof TopUp>;
